import { Router } from "express";
import { Routes } from "interface/routes.interface";
import { permissionInjector } from "middlewares/permissionInjector";
import { UserManagementController } from "../controllers/userManagement.controller";
import { authMiddleware, restrictToSelfOrAdminCreator } from "../../../middlewares/authMiddleware";

/**
 * @swagger
 * tags:
 *   name: Users
 *   description: User management endpoints
 */
export class UserManagementRoute implements Routes {
    public path = '/user';
    public router = Router();
    public userManagementController = new UserManagementController();

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {
        this.router.post(
            `${this.path}/grant/:id`,
            permissionInjector(`${this.path}/grant`, 'POST'),
            this.userManagementController.grantPermission
        );
        this.router.post(
            `${this.path}/revoke/:id`,
            permissionInjector(`${this.path}/revoke`, 'POST'),
            this.userManagementController.revokPermission
        );
        this.router.post(
            `${this.path}/restrict/:id`,
            permissionInjector(`${this.path}/restrict`, 'POST'),
            this.userManagementController.restricPermission
        );
        /**
         * @swagger
         * /user/Me:
         *  get:
         *     summary: Get Current User's profile
         *     tags: [Users]
         *     security:
         *      - bearerAuth: []
         */
        this.router.get(
            `${this.path}/Me`,
            authMiddleware,
            this.userManagementController.getMyProfile
        );
        /**
         * @swagger
         * /user/{id}:
         *   get:
         *     summary: Get user by ID
         *     tags: [Users]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: User ID
         *     responses:
         *       200:
         *         description: User retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: success
         *                 message:
         *                   type: string
         *                   example: User retrieved successfully
         *                 data:
         *                   $ref: '#/components/schemas/User'
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: User not found
         */
        this.router.get(
            `${this.path}/:id`,
            authMiddleware,
            // permissionInjector(`${this.path}`, 'GET'),
            this.userManagementController.getByUserId
        );
        /**
         * @swagger
         * /user/resend-invitation:
         *   post:
         *     summary: Create a new user
         *     tags: [Users]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required: [email]
         *             properties:
         *               email:
         *                 type: string
         *                 format: email
         *                 example: user@example.com
         *     responses:
         *       200:
         *         description: Successfully sent email
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: success
         *                 message:
         *                   type: string
         *                   example: Successfully sent email
         *                 data:
         *                   $ref: '#/components/schemas/User'
         *       400:
         *         description: Invalid input
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       409:
         *         description: Email already exists
         */
        this.router.post(
            `${this.path}/resend-invitation`,
            permissionInjector(`${this.path}`, 'POST'),
            this.userManagementController.resendInvitation
        );


        /**
         * @swagger
         * /user:
         *   post:
         *     summary: Create a new user
         *     tags: [Users]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: type
         *         requried: true
         *         schema:
         *          type: string
         *         description: Role name (e.g., Labour, Staff)
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required: [email, firstName, lastName]
         *             properties:
         *               email:
         *                 type: string
         *                 format: email
         *                 example: user@example.com
         *               firstName:
         *                 type: string
         *                 example: John
         *               lastName:
         *                 type: string
         *                 example: Doe
         *               profileImage:
         *                 type: string
         *                 example: 507f1f77bcf86cd799439012
         *     responses:
         *       200:
         *         description: User created successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: success
         *                 message:
         *                   type: string
         *                   example: User added successfully
         *                 data:
         *                   $ref: '#/components/schemas/User'
         *       400:
         *         description: Invalid input
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       409:
         *         description: Email already exists
         */
        this.router.post(
            `${this.path}`,
            permissionInjector(`${this.path}`, 'POST'),
            this.userManagementController.createUser
        );

        /**
         * @swagger
         * /user:
         *   get:
         *     summary: Get all users
         *     tags: [Users]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 50
         *         description: Number of users per page
         *       - in: query
         *         name: search
         *         schema:
         *           type: string
         *         description: Search by name or email
         *       - in: query
         *         name: role
         *         schema:
         *           type: string
         *         description: Filter by role ID or name
         *       - in: query
         *         name: sortBy
         *         schema:
         *           type: string
         *           default: firstName
         *         description: Field to sort by
         *       - in: query
         *         name: sortOrder
         *         schema:
         *           type: string
         *           default: asc
         *         description: Sort order (asc/desc)
         *     responses:
         *       200:
         *         description: Users retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: success
         *                 message:
         *                   type: string
         *                   example: Users retrieved successfully
         *                 data:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/User'
         *                 total:
         *                   type: integer
         *                   example: 100
         *                 page:
         *                   type: integer
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   example: 2
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         */
        this.router.get(
            `${this.path}`,
            authMiddleware,
            // permissionInjector(`${this.path}`, 'GET'),
            this.userManagementController.getAllUsers
        );
        /**
         * @swagger
         * /user:
         *   delete:
         *     summary: Delete a user
         *     tags: [Users]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required: [ids]
         *             properties:
         *               ids:
         *                 type: array
         *                 example: ["ids"]
         *     responses:
         *       200:
         *         description: User deleted successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: success
         *                 message:
         *                   type: string
         *                   example: User deleted successfully
         *                 deletedCount:
         *                   type: integer
         *                   example: 1
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: User not found
         */
        this.router.delete(
            `${this.path}`,
            authMiddleware,
            permissionInjector(`${this.path}`, 'DELETE'),
            this.userManagementController.deleteUser
        );
        /**
         * @swagger
         * /user/{id}:
         *   put:
         *    summary: Update user
         *    tags: [Users]
         *    security:
         *      - bearerAuth: []
         *    parameters:
         *      - in: path
         *        name: id
         *        required: true
         *        shcema: 
         *         type: string
         *        description: User ID
         *    requestBody:
         *      required: true
         *      content:
         *       application/json:
         *        schema:
         *         type: object
         *         properties:
         *          firstName:
         *            type: string
         *            example: John 
         *          lastName:
         *            type: string
         *            example: John 
         *          phone:
         *             type: string
         *             example: "9876543210" 
         *          countryCode:
         *             type: string
         *             example: +91
         *          gender:
         *             type: string
         *             enum: [male, female, other]
         *             example: male
         *          date_of_birth:
         *             type: string
         *             format: date
         *             example: 1990-02-02
         *          profileImage:
         *             type: string
         *             enum: ID of the uploaded profile image (attachment ID)
         *             example: 60c72b2f9b1e8c1f88f12345
         *    responses:
         *      200:
         *        description: Profile updated successfully
         *        content:
         *          application/json:
         *            schema:
         *              type: object
         *              properties:
         *                status:
         *                  type: string
         *                  example: success
         *                message:
         *                  type: string
         *                  example: Profile updated successfully
         *                data:
         *                  type: object
         *                  properties:
         *                   _id:
         *                    type: string
         *                    example: 60b8d6e72f8fb814c89e0e12
         *                   email:
         *                    type: string
         *                    example: john.doe@example.com
         *                   firstName:
         *                    type: string
         *                    example: John
         *                   phone:
         *                    type: string
         *                    example: "9876543210"
         *                   countryCode:
         *                    type: string
         *                    example: +91
         *                   gender:
         *                    type: string
         *                    example: male
         *                   date_of_birth:
         *                    type: string
         *                    example: 1990-01-01
         *                   profileImage:
         *                    type: object
         *                    nullable: true
         *                    properties:
         *                      _id: 
         *                       type: string
         *                       example: 60c72b2f9b1e8c1f88f12345
         *                      filename: 
         *                       type: string
         *                       example: profile.jpg
         *                      url: 
         *                       type: string
         *                       example: https://your-bucket.s3.amazonaws.com/profile.jpg
         *                   createdBy:
         *                    type: string
         *                    example: 60a70ef4c25e123abc456def
         *                   roleId:
         *                    type: string
         *                    example: 60a72ef8c1f4b3abc1234567
         *      401:
         *        description: Unauthorized
         *      403:
         *        description: Forbidden
         *      404:
         *        description: User not found
         */

        this.router.put(
            `${this.path}/:id`,
            authMiddleware,
            restrictToSelfOrAdminCreator,
            permissionInjector(`${this.path}`, 'PUT'),
            this.userManagementController.updateProfile
        );
        this.router.patch(
            `${this.path}/changeStatus/:id`,
            authMiddleware,
            permissionInjector(`${this.path}/changeStatus`, 'PATCH'),
            this.userManagementController.changeStatus
        );
    }
}


/**
 * @swagger
 * components:
 *   schemas:
 *     User:
 *       type: object
 *       properties:
 *         _id:
 *           type: string
 *           example: 507f1f77bcf86cd799439011
 *         email:
 *           type: string
 *           format: email
 *           example: user@example.com
 *         firstName:
 *           type: string
 *           example: John
 *         lastName:
 *           type: string
 *           example: Doe
 *         fullName:
 *           type: string
 *           example: John Doe
 *         roleName:
 *           type: string
 *           example: LABOUR
 *         profileImage:
 *           type: object
 *           nullable: true
 *           properties:
 *             _id:
 *               type: string
 *               example: 507f1f77bcf86cd799439012
 *             filename:
 *               type: string
 *               example: profile.jpg
 *             url:
 *               type: string
 *               example: https://s3.amazonaws.com/bucket/profile.jpg
 *         status:
 *           type: string
 *           example: ACTIVE
 */